import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { cn } from "@/lib/utils";
import { useConversationsWithShadow, useConversationMessages } from "@/hooks";
import {
  ConversationList,
  ConversationDetail,
  ConversationDetailsSidebar,
  type ConversationFilters as ConversationFiltersType,
} from "@/modules/shadow/components";
import {
  MessageSquare,
  ChevronDown,
  ArrowUpDown,
  Check,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, SearchInput } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useRulesAndKbs } from "@/hooks/use-rules-and-kbs";
import { requestShadowReportEmail } from "@/services/report.service";
import { selectedBusinessUnitIdAtom } from "@/stores/business-unit.store";
import { toast } from "sonner";
import { isAfter, startOfDay } from "date-fns";
import type { ResponseQuality } from "@/modules/shadow/constants";
import { RESPONSE_QUALITY_OPTIONS } from "@/modules/shadow/constants";

const DEBOUNCE_DELAY = 300;

export default function ShadowConversations() {
  const [selectedConversationId, setSelectedConversationId] = useState<
    number | undefined
  >();
  const [filters, setFilters] = useState<ConversationFiltersType>({
    sortOrder: "desc",
  });
  const [isExporting, setIsExporting] = useState(false);
  const { currentUser } = useAuth();
  const { can } = usePermissions();
  const canSendReports = can('view', 'reports');
  // Same atom the list query reads, so the emailed report matches the list on screen.
  const selectedBuId = useAtomValue(selectedBusinessUnitIdAtom);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [pendingReportType, setPendingReportType] = useState<
    "raw" | "summary" | null
  >(null);
  const [emailInput, setEmailInput] = useState<string>(
    currentUser?.email ?? "",
  );
  const [searchInput, setSearchInput] = useState("");
  const [isSortPopoverOpen, setIsSortPopoverOpen] = useState(false);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<
    string | undefined
  >();
  const [isRulesKbsPopoverOpen, setIsRulesKbsPopoverOpen] = useState(false);
  const [isResponseQualityPopoverOpen, setIsResponseQualityPopoverOpen] =
    useState(false);
  const [selectedResponseQualities, setSelectedResponseQualities] = useState<
    ResponseQuality[]
  >([]);
  const [selectedRulesKbs, setSelectedRulesKbs] = useState<{
    rules: string[];
    kbs: string[];
  }>({ rules: [], kbs: [] });

  const { data: rulesAndKbsData, isLoading: rulesAndKbsLoading } =
    useRulesAndKbs();

  const {
    data: conversations = [],
    totalCount: conversationsTotalCount = 0,
    isLoading: conversationsLoading,
    hasMore: conversationsHasMore,
    isFetchingNextPage: conversationsFetchingNextPage,
    fetchNextPage: fetchNextConversationsPage,
  } = useConversationsWithShadow(filters);

  const {
    data: messages = [],
    isLoading: messagesLoading,
    hasMore: messagesHasMore,
    isFetchingNextPage: messagesFetchingNextPage,
    fetchNextPage: fetchNextMessagesPage,
  } = useConversationMessages(selectedConversationId);

  // Sync search input with filters when they change externally
  useEffect(() => {
    setSearchInput(filters.contactSearch || "");
  }, [filters.contactSearch]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (filters.contactSearch || "")) {
        setFilters((prev) => ({
          ...prev,
          contactSearch: searchInput || undefined,
        }));
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchInput, filters.contactSearch]);

  // Update filters when rules/kbs selection changes (with debouncing)
  useEffect(() => {
    const timer = setTimeout(() => {
      const rulesAndKbs: Array<{ type: "rule" | "kb"; ids: string[] }> = [];
      if (selectedRulesKbs.rules.length > 0) {
        rulesAndKbs.push({ type: "rule", ids: selectedRulesKbs.rules });
      }
      if (selectedRulesKbs.kbs.length > 0) {
        rulesAndKbs.push({ type: "kb", ids: selectedRulesKbs.kbs });
      }

      setFilters((prev) => ({
        ...prev,
        ruleId: undefined, // Remove ruleId when using rulesAndKbs
        rulesAndKbs: rulesAndKbs.length > 0 ? rulesAndKbs : undefined,
      }));
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [selectedRulesKbs]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      responseQuality:
        selectedResponseQualities.length > 0
          ? selectedResponseQualities
          : undefined,
    }));
  }, [selectedResponseQualities]);

  const handleSelectConversation = (conversationId: number) => {
    setSelectedConversationId(conversationId);
    // Close right panel when switching conversations
    setIsRightPanelOpen(false);
    setSelectedMessageId(undefined);
  };

  const handleMessageAnalyticsClick = (messageId: string) => {
    setSelectedMessageId(messageId);
    setIsRightPanelOpen(true);
  };

  const handleDateRangeChange = (range: {
    startDate?: Date;
    endDate?: Date;
  }) => {
    setFilters({
      ...filters,
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const isDateDisabled = (date: Date) => {
    return isAfter(startOfDay(date), startOfDay(new Date()));
  };

  const openEmailDialog = (reportType: "raw" | "summary") => {
    setPendingReportType(reportType);
    setIsEmailDialogOpen(true);
    if (!emailInput && currentUser?.email) {
      setEmailInput(currentUser.email);
    }
  };

  const handleConfirmExport = async () => {
    if (!pendingReportType) return;
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      toast.error("Please enter an email address to send the report.");
      return;
    }

    try {
      setIsExporting(true);
      await requestShadowReportEmail({
        email: trimmedEmail,
        reportType: pendingReportType,
        filters: {
          ...filters,
        },
        scope: selectedBuId ? { bu_id: selectedBuId } : {},
      });
      toast.success(
        "Report is being generated and will be emailed to you shortly.",
      );
    } catch (error: unknown) {
      const message =
        (error as { message?: string })?.message ??
        "Failed to queue report export";
      toast.error(message);
    } finally {
      setIsExporting(false);
      setIsEmailDialogOpen(false);
      setPendingReportType(null);
    }
  };

  const getConversationEmail = () => {
    const selectedConvo = conversations.find(
      (convo) => convo.conversationId === selectedConversationId,
    );

    return selectedConvo?.contact?.email;
  };

  const getConversationCategory = () => {
    const selectedConvo = conversations.find(
      (convo) => convo.conversationId === selectedConversationId,
    );

    return selectedConvo?.categories?.[0] || null;
  };

  const getConversationChannel = () => {
    const selectedConvo = conversations.find(
      (convo) => convo.conversationId === selectedConversationId,
    );

    return selectedConvo?.channel || null;
  };

  const handleRuleKbToggle = (id: string, type: "rule" | "kb") => {
    setSelectedRulesKbs((prev) => {
      const key = type === "rule" ? "rules" : "kbs";
      const current = prev[key];
      const newSelection = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];

      return { ...prev, [key]: newSelection };
    });
  };

  const clearRulesKbsSelection = () => {
    setSelectedRulesKbs({ rules: [], kbs: [] });
  };

  const handleResponseQualityToggle = (quality: ResponseQuality) => {
    setSelectedResponseQualities((prev) =>
      prev.includes(quality)
        ? prev.filter((item) => item !== quality)
        : [...prev, quality],
    );
  };

  const clearResponseQualitySelection = () => {
    setSelectedResponseQualities([]);
  };

  const getSelectedCount = () => {
    return selectedRulesKbs.rules.length + selectedRulesKbs.kbs.length;
  };

  const getSelectedLabel = (): string => {
    const totalRules = rulesAndKbsData?.rules?.length ?? 0;
    const totalKbs = rulesAndKbsData?.knowledgeBases?.length ?? 0;
    const selRules = selectedRulesKbs.rules.length;
    const selKbs = selectedRulesKbs.kbs.length;

    if (selRules === 0 && selKbs === 0) return "All Instructions & KBs";

    // Check if all are selected
    if (
      selRules === totalRules &&
      selKbs === totalKbs &&
      totalRules + totalKbs > 0
    ) {
      return "All Selected";
    }

    const parts: string[] = [];
    if (selKbs > 0) parts.push(`${selKbs} KB`);
    if (selRules > 0) parts.push(`${selRules} Instruction`);

    return `${parts.join(" & ")} Selected`;
  };

  const getResponseQualityLabel = (): string => {
    const selectedCount = selectedResponseQualities.length;
    const totalCount = RESPONSE_QUALITY_OPTIONS.length;

    if (selectedCount === 0) return "All Response Quality";
    if (selectedCount === totalCount) return "All Selected";
    if (selectedCount === 1) return selectedResponseQualities[0];
    return `${selectedCount} Selected`;
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden relative">
      {/* Header */}
      <div className="sticky top-0 p-4 border-b border-gray-400 bg-white/70 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-base font-semibold text-black-300">
            AI Shadow Audit
          </h1>
          <div className="flex items-center gap-3">
            {/* Date Range Picker */}
            <div className="min-w-[280px]">
              <DateRangePicker
                value={{
                  startDate: filters.startDate,
                  endDate: filters.endDate,
                }}
                onChange={handleDateRangeChange}
                disabled={isDateDisabled}
                placeholder={{ start: "Start date", end: "End date" }}
              />
            </div>

            {/* Rules & KBs Filter */}
            <div className="w-[250px]">
              <Popover
                open={isRulesKbsPopoverOpen}
                onOpenChange={setIsRulesKbsPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={rulesAndKbsLoading}
                    className={cn(
                      "w-full h-8 justify-between text-xs bg-white border border-gray-400 rounded-3xl px-3 font-normal hover:bg-gray-50",
                      getSelectedCount() > 0 && "text-black-400",
                    )}
                  >
                    <span className="whitespace-nowrap">
                      {rulesAndKbsLoading ? "Loading..." : getSelectedLabel()}
                    </span>
                    <ChevronDown
                      size={12}
                      className="ml-2 flex-shrink-0 opacity-50"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="relative top-2 bg-white/80 backdrop-blur-sm w-[280px] p-0 border border-gray-400 rounded-xl"
                  align="start"
                >
                  <style>{`
                    #rules-kb-scroll::-webkit-scrollbar { width: 10px; }
                    #rules-kb-scroll::-webkit-scrollbar-track { background: transparent; }
                    #rules-kb-scroll::-webkit-scrollbar-thumb {
                      background: var(--color-gray-400);
                      border-radius: 9999px;
                      border: 3px solid transparent;
                      background-clip: content-box;
                    }
                    #rules-kb-scroll::-webkit-scrollbar-button {
                      display: block;
                      height: 4px;
                      background: transparent;
                    }
                  `}</style>
                  <div
                    id="rules-kb-scroll"
                    className="max-h-[500px] overflow-y-auto"
                  >
                    {/* Header with Clear button */}
                    {getSelectedCount() > 0 && (
                      <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b px-3 py-2 flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">
                          {getSelectedCount()} selected
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearRulesKbsSelection}
                          className="h-6 px-2 text-xs"
                        >
                          Clear
                        </Button>
                      </div>
                    )}

                    {/* Instructions Section */}
                    {rulesAndKbsData?.rules &&
                      rulesAndKbsData.rules.length > 0 && (
                        <div className="p-2">
                          <div className="px-2 py-1.5 text-[0.625rem] font-medium text-gray-700">
                            Instructions
                          </div>
                          {rulesAndKbsData.rules.map((rule) => {
                            const isSelected = selectedRulesKbs.rules.includes(
                              rule.id,
                            );
                            return (
                              <div
                                key={rule.id}
                                onClick={() =>
                                  handleRuleKbToggle(rule.id, "rule")
                                }
                                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-gray-100 cursor-pointer"
                              >
                                <span className="text-xs flex-1 truncate text-black-400 font-normal capitalize">
                                  {rule.name}
                                </span>
                                {isSelected && (
                                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                    {/* Knowledge Base Section */}
                    {rulesAndKbsData?.knowledgeBases &&
                      rulesAndKbsData.knowledgeBases.length > 0 && (
                        <div className="p-2 border-t border-dashed border-gray-500">
                          <div className="px-2 py-1.5 text-[0.625rem] font-medium text-gray-700">
                            Knowledge Base
                          </div>
                          {rulesAndKbsData.knowledgeBases.map((kb) => {
                            const isSelected = selectedRulesKbs.kbs.includes(
                              kb.id,
                            );
                            return (
                              <div
                                key={kb.id}
                                onClick={() => handleRuleKbToggle(kb.id, "kb")}
                                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-gray-100 cursor-pointer"
                              >
                                <span className="text-xs flex-1 text-black-400 capitalize truncate ">
                                  {kb.name}
                                </span>
                                {isSelected && (
                                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                    {/* Empty state */}
                    {(!rulesAndKbsData?.rules ||
                      rulesAndKbsData.rules.length === 0) &&
                      (!rulesAndKbsData?.knowledgeBases ||
                        rulesAndKbsData.knowledgeBases.length === 0) && (
                        <div className="p-4 text-center text-xs text-gray-500">
                          No instructions or knowledge bases available
                        </div>
                      )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Response Quality Filter */}
            <div className="w-[220px]">
              <Popover
                open={isResponseQualityPopoverOpen}
                onOpenChange={setIsResponseQualityPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-full h-8 justify-between text-xs bg-white border border-gray-400 rounded-3xl px-3 font-normal hover:bg-gray-50",
                      selectedResponseQualities.length > 0 && "text-black-400",
                    )}
                  >
                    <span className="whitespace-nowrap truncate">
                      {getResponseQualityLabel()}
                    </span>
                    <ChevronDown
                      size={12}
                      className="ml-2 flex-shrink-0 opacity-50"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="relative top-2 bg-white/80 backdrop-blur-sm w-[240px] p-0 border border-gray-400 rounded-xl"
                  align="start"
                >
                  {selectedResponseQualities.length > 0 && (
                    <div className="sticky top-0 z-10 bg-white rounded-t-2xl border-b px-3 py-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        {selectedResponseQualities.length} selected
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearResponseQualitySelection}
                        className="h-6 px-2 text-xs"
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                  <div className="p-2">
                    <div className="px-2 py-1.5 text-[0.625rem] font-medium text-gray-700">
                      Comparison Bucket
                    </div>
                    {RESPONSE_QUALITY_OPTIONS.map((quality) => {
                      const isSelected =
                        selectedResponseQualities.includes(quality);
                      return (
                        <div
                          key={quality}
                          onClick={() => handleResponseQualityToggle(quality)}
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-gray-100 cursor-pointer"
                        >
                          <span className="text-xs flex-1 truncate text-black-400 font-normal">
                            {quality}
                          </span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Download Dropdown */}
            {canSendReports && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isExporting}
                    className="gap-1 h-8 text-xs px-2"
                  >
                    Download
                    <ChevronDown size={10} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="cursor-pointer text-xs"
                    onClick={() => openEmailDialog("raw")}
                  >
                    Email Raw Report
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer text-xs"
                    onClick={() => openEmailDialog("summary")}
                  >
                    Email Summary Report
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="flex-1 min-h-0 overflow-hidden flex">
        {/* Left Panel - Conversation List */}
        <div className="w-[21.5%] max-w-[320px] h-full relative overflow-hidden min-w-0 border-r border-gray-300 shrink-0">
          <div className="absolute z-1 top-0 left-0 right-0 pt-3 pl-3 pr-2 bg-white/70 backdrop-blur-sm border-gray-200">
            <div className="mb-3 mr-1 flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <h2 className="font-semibold text-sm text-black-300">
                  Conversations
                </h2>
                <p className="text-xs text-gray-700">
                  {conversationsTotalCount?.toLocaleString()} Ticket
                  {conversationsTotalCount !== 1 ? "s" : ""}
                </p>
              </div>
              <Popover
                open={isSortPopoverOpen}
                onOpenChange={setIsSortPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 hover:bg-gray-300"
                  >
                    <ArrowUpDown className="h-4 w-4 text-gray-700" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-44 p-1" align="end">
                  <div className="space-y-1">
                    <button
                      onClick={() => {
                        setFilters({ ...filters, sortOrder: "desc" });
                        setIsSortPopoverOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-3 py-2 text-xs rounded-md cursor-pointer hover:bg-gray-300 transition-colors",
                        filters.sortOrder === "desc" && "bg-gray-100",
                      )}
                    >
                      Newest First
                    </button>
                    <button
                      onClick={() => {
                        setFilters({ ...filters, sortOrder: "asc" });
                        setIsSortPopoverOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-3 py-2 text-xs rounded-md cursor-pointer hover:bg-gray-300 transition-colors",
                        filters.sortOrder === "asc" && "bg-gray-100",
                      )}
                    >
                      Oldest First
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <SearchInput
              placeholder="Search by Name, Email or conversation ID"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full border-gray-300 text-ellipsis h-8"
            />
            <div className="absolute -bottom-10 h-10 bg-linear-to-b from-white to-transparent backdrop-blur-xl w-full -ml-3" />
          </div>
          <div className="absolute inset-0 w-full min-w-0">
            <ConversationList
              conversations={conversations}
              selectedConversationId={selectedConversationId}
              onSelectConversation={handleSelectConversation}
              isLoading={conversationsLoading}
              hasMore={conversationsHasMore}
              isFetchingNextPage={conversationsFetchingNextPage}
              onLoadMore={fetchNextConversationsPage}
            />
          </div>
        </div>

        {/* Middle Panel - Conversation Detail */}
        <div className="flex-1 h-full min-w-0 relative">
          {selectedConversationId ? (
            <>
              <div className="absolute inset-0 overflow-hidden min-w-0">
                <ConversationDetail
                  messages={Array.isArray(messages) ? [...messages] : []}
                  isLoading={messagesLoading}
                  hasMore={messagesHasMore}
                  isFetchingNextPage={messagesFetchingNextPage}
                  onLoadMore={fetchNextMessagesPage}
                  onMessageAnalyticsClick={handleMessageAnalyticsClick}
                  selectedMessageId={selectedMessageId}
                />
              </div>
              <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between max-w-full w-full border-b border-gray-400 bg-white/70 backdrop-blur-sm">
                <div className="flex-shrink-0 p-3">
                  <h2 className="font-semibold text-base text-black-300">
                    # {selectedConversationId}
                  </h2>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground font-normal">
                    {getConversationChannel() === "Email" ? (
                      <Mail color="var(--color-gray-800)" className="h-3 w-3" />
                    ) : (
                      <MessageSquare fill="var(--color-gray-800)" className="h-3 w-3" />
                    )}
                    <p>{getConversationEmail()}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 p-3">
                  {getConversationCategory() ? (
                    <div className="flex items-center gap-2 text-accent-purple border border-gray-400 rounded-sm w-fit py-1.5 px-3 h-6 text-xs">
                      <span className="h-1.5 w-1.5 bg-accent-purple rounded-[1px]" />
                      {getConversationCategory()}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500">-</span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Select a conversation to view messages
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Customer & Ticket Details */}
        <div
          className={cn(
            "h-full flex flex-col overflow-hidden bg-white border-l border-gray-300 shrink-0 transition-all duration-200 ease-in-out",
            isRightPanelOpen
              ? "w-[21.5%] max-w-[320px] opacity-100"
              : "w-0 opacity-0",
          )}
          style={{
            willChange: isRightPanelOpen ? "auto" : "width",
          }}
        >
          {isRightPanelOpen && (
            <>
              <div className="flex-1 min-h-0 overflow-hidden">
                <ConversationDetailsSidebar
                  messages={Array.isArray(messages) ? [...messages] : []}
                  conversationId={selectedConversationId}
                  contact={
                    conversations.find(
                      (c) => c.conversationId === selectedConversationId,
                    )?.contact || null
                  }
                  isLoading={messagesLoading}
                  onSelectConversation={handleSelectConversation}
                  selectedMessageId={selectedMessageId}
                  onMessageSelect={handleMessageAnalyticsClick}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="max-w-md w-full p-5 overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {pendingReportType === "summary"
                ? "Email Summary Report"
                : "Email Raw Report"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Enter the email address where you want to receive the report.
            </p>
            <Input
              type="email"
              placeholder="you@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEmailDialogOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmExport} disabled={isExporting}>
              {isExporting ? "Queuing..." : "Send Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
